import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.archiveabyss.com';

async function getAuthHeader(): Promise<string> {
  const session = await fetchAuthSession();
  return `Bearer ${session.tokens?.idToken?.toString()}`;
}

async function apiRequest<T>(
  method: string,
  path: string,
  body?: object
): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      'Authorization': await getAuthHeader(),
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || `HTTP ${response.status}`);
  }

  return data;
}

// User Management
export interface User {
  username: string;
  email: string;
  status: string;
  enabled: boolean;
  created: string;
  modified: string;
}

export interface CreateUserResponse {
  message: string;
  email: string;
  temporaryPassword: string;
  note: string;
}

export const adminApi = {
  // Users
  listUsers: () => apiRequest<{ users: User[] }>('GET', '/admin/users'),

  createUser: (email: string) =>
    apiRequest<CreateUserResponse>('POST', '/admin/users', { email }),

  deleteUser: (email: string) =>
    apiRequest<{ message: string }>('DELETE', `/admin/users/${encodeURIComponent(email)}`),

  resetPassword: (email: string) =>
    apiRequest<{ message: string; temporaryPassword: string }>('POST', `/admin/users/${encodeURIComponent(email)}/reset`),

  // Tenants
  listTenants: () => apiRequest<{ tenants: Tenant[] }>('GET', '/admin/tenants'),

  createTenant: (data: { tenant_id: string; name: string; subdomain?: string }) =>
    apiRequest<{ tenant: Tenant }>('POST', '/admin/tenants', data),

  updateTenant: (id: string, data: Partial<Tenant>) =>
    apiRequest<{ tenant: Tenant }>('PUT', `/admin/tenants/${encodeURIComponent(id)}`, data),

  deleteTenant: (id: string) =>
    apiRequest<{ message: string }>('DELETE', `/admin/tenants/${encodeURIComponent(id)}`),

  // Branding
  getBranding: (tenantId?: string) =>
    apiRequest<{ tenant_id: string; branding: TenantBranding }>(
      'GET',
      tenantId ? `/admin/branding?tenant_id=${tenantId}` : '/admin/branding'
    ),

  updateBranding: (branding: Partial<TenantBranding>, tenantId?: string) =>
    apiRequest<{ message: string; branding: TenantBranding }>(
      'PUT',
      '/admin/branding',
      { branding, tenant_id: tenantId }
    ),

  getUploadUrl: (type: 'logo' | 'favicon', contentType: string, tenantId?: string) =>
    apiRequest<{ uploadUrl: string; publicUrl: string; key: string }>(
      'POST',
      '/admin/branding/upload',
      { type, content_type: contentType, tenant_id: tenantId }
    ),
};

export interface Tenant {
  tenant_id: string;
  subdomain: string;
  name: string;
  status: string;
  created_at: string;
  branding?: TenantBranding;
  settings?: TenantSettings;
}

export interface TenantBranding {
  company_name: string;
  primary_color: string;
  accent_color: string;
  background_color: string;
  logo_url: string;
  favicon_url: string;
}

export interface TenantSettings {
  max_users: number;
  features_enabled: string[];
}
